import { MEDIA_ACCEPTED_TYPES, MEDIA_MAX_BYTES, MEDIA_MAX_PER_PROPERTY } from '@erp/shared';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  ImagePlus,
  LoaderCircle,
  Lock,
  LockOpen,
  Ruler,
  Star,
  Trash2,
  TriangleAlert,
} from 'lucide-react';
import { useRef, useState, type DragEvent } from 'react';

import { api, errorMessage } from '../../lib/api.ts';
import { cn } from '../../lib/cn.ts';
import { propertyKeys, type PropertyDetail, type PropertyMedia } from '../../lib/properties-api.ts';
import { useToast } from '../../lib/toast.tsx';
import { Badge } from '../ui/badge.tsx';
import { Button } from '../ui/button.tsx';
import { Card } from '../ui/card.tsx';
import { Dialog } from '../ui/dialog.tsx';

/**
 * Photos and plans of a property (HU-INV-08, HU-INV-11). Several files are uploaded
 * one after the other; the worker processes them (WebP, no EXIF) while the card
 * shows "procesando". Order: drag on a computer, arrows on a phone or keyboard. The
 * cover is the first public photo; private ones never leave the agency.
 */
export function MediaManager({ property }: { property: PropertyDetail }) {
  const client = useQueryClient();
  const notify = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<{ done: number; total: number } | null>(null);
  const [viewing, setViewing] = useState<PropertyMedia | null>(null);
  const [dragged, setDragged] = useState<string | null>(null);
  const editable = property.can.edit;
  const items = property.media;
  const coverId = items.find((m) => m.kind === 'photo' && !m.isPrivate && m.status === 'ready')?.id;

  const setMedia = (media: PropertyMedia[]) => {
    client.setQueryData<PropertyDetail>(propertyKeys.detail(property.id), (current) =>
      current ? { ...current, media } : current,
    );
    return client.invalidateQueries({ queryKey: propertyKeys.all });
  };

  const upload = async (files: File[]) => {
    const room = MEDIA_MAX_PER_PROPERTY - items.length;
    if (files.length > room) {
      notify(`Solo caben ${String(room)} fotos más en esta propiedad.`, 'error');
      files = files.slice(0, Math.max(room, 0));
    }
    const accepted = files.filter((file) => {
      if (!(MEDIA_ACCEPTED_TYPES as readonly string[]).includes(file.type)) {
        notify(`«${file.name}»: formato no admitido (JPEG, PNG o WebP).`, 'error');
        return false;
      }
      if (file.size > MEDIA_MAX_BYTES) {
        notify(`«${file.name}» supera los 20 MB.`, 'error');
        return false;
      }
      return true;
    });
    if (accepted.length === 0) return;
    setUploading({ done: 0, total: accepted.length });
    let failed = 0;
    for (const [index, file] of accepted.entries()) {
      const body = new FormData();
      body.append('file', file);
      try {
        await api(`/properties/${property.id}/media`, { method: 'POST', body });
      } catch (error) {
        failed += 1;
        notify(`«${file.name}»: ${errorMessage(error)}`, 'error');
      }
      setUploading({ done: index + 1, total: accepted.length });
    }
    setUploading(null);
    await client.invalidateQueries({ queryKey: propertyKeys.detail(property.id) });
    if (failed < accepted.length) {
      notify(
        accepted.length - failed === 1
          ? 'Foto subida; se está procesando.'
          : `${String(accepted.length - failed)} fotos subidas; se están procesando.`,
        'success',
      );
    }
  };

  const reorder = async (ids: string[]) => {
    try {
      await setMedia(
        await api<PropertyMedia[]>(`/properties/${property.id}/media/order`, {
          method: 'POST',
          body: { ids },
        }),
      );
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const move = (id: string, delta: number) => {
    const ids = items.map((m) => m.id);
    const from = ids.indexOf(id);
    const to = from + delta;
    if (from < 0 || to < 0 || to >= ids.length) return;
    ids.splice(to, 0, ...ids.splice(from, 1));
    void reorder(ids);
  };
  const makeCover = (id: string) => {
    void reorder([id, ...items.map((m) => m.id).filter((m) => m !== id)]);
  };
  const change = async (media: PropertyMedia, body: Record<string, unknown>) => {
    try {
      await setMedia(
        await api<PropertyMedia[]>(`/properties/${property.id}/media/${media.id}`, {
          method: 'PATCH',
          body,
        }),
      );
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };
  const remove = async (media: PropertyMedia) => {
    try {
      await setMedia(
        await api<PropertyMedia[]>(`/properties/${property.id}/media/${media.id}`, {
          method: 'DELETE',
        }),
      );
      notify('Foto eliminada.', 'success');
    } catch (error) {
      notify(errorMessage(error), 'error');
    }
  };

  const onDrop = (event: DragEvent, targetId: string) => {
    event.preventDefault();
    if (!dragged || dragged === targetId) return;
    const ids = items.map((m) => m.id).filter((id) => id !== dragged);
    ids.splice(ids.indexOf(targetId), 0, dragged);
    setDragged(null);
    void reorder(ids);
  };

  return (
    <Card className="space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="font-semibold">Fotos y planos</h2>
          <p className="text-sm text-muted-foreground">
            {items.length === 0
              ? 'Todavía no hay fotos.'
              : `${String(items.length)} de ${String(MEDIA_MAX_PER_PROPERTY)}. La portada es la primera foto pública; las privadas nunca salen de la inmobiliaria.`}
          </p>
        </div>
        {editable && (
          <>
            <input
              ref={input}
              type="file"
              multiple
              accept={MEDIA_ACCEPTED_TYPES.join(',')}
              className="sr-only"
              aria-label="Elegir fotos para subir"
              tabIndex={-1}
              onChange={(event) => {
                const files = [...(event.target.files ?? [])];
                event.target.value = '';
                void upload(files);
              }}
            />
            <Button
              variant="brand"
              loading={uploading !== null}
              onClick={() => input.current?.click()}
            >
              <ImagePlus /> Subir fotos
            </Button>
          </>
        )}
      </div>
      {uploading && (
        <p className="text-sm text-muted-foreground" role="status">
          Subiendo {uploading.done + 1 > uploading.total ? uploading.total : uploading.done + 1} de{' '}
          {uploading.total}…
        </p>
      )}
      {items.length > 0 && (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {items.map((media, index) => (
            <li
              key={media.id}
              draggable={editable}
              onDragStart={() => {
                setDragged(media.id);
              }}
              onDragOver={(event) => {
                if (dragged) event.preventDefault();
              }}
              onDrop={(event) => {
                onDrop(event, media.id);
              }}
              className={cn(
                'overflow-hidden rounded-lg border border-border bg-muted',
                dragged === media.id && 'opacity-50',
              )}
            >
              <div className="relative aspect-[4/3]">
                {media.status === 'ready' && media.thumbUrl ? (
                  <button
                    type="button"
                    className="block size-full"
                    aria-label={`Ver ${media.kind === 'plan' ? 'plano' : 'foto'} ${String(index + 1)}`}
                    onClick={() => {
                      setViewing(media);
                    }}
                  >
                    <img
                      src={media.thumbUrl}
                      alt=""
                      className="size-full object-cover"
                      loading="lazy"
                      draggable={false}
                    />
                  </button>
                ) : (
                  <div className="flex size-full flex-col items-center justify-center gap-1 p-2 text-center text-xs text-muted-foreground">
                    {media.status === 'failed' ? (
                      <>
                        <TriangleAlert className="size-5 text-danger" aria-hidden="true" />
                        No se pudo procesar; elimínala y súbela de nuevo.
                      </>
                    ) : (
                      <>
                        <LoaderCircle className="size-5 animate-spin" aria-hidden="true" />
                        Procesando…
                      </>
                    )}
                  </div>
                )}
                <div className="absolute top-1.5 left-1.5 flex flex-wrap gap-1">
                  {media.id === coverId && <Badge tone="info">Portada</Badge>}
                  {media.isPrivate && <Badge tone="warning">Privada</Badge>}
                  {media.kind === 'plan' && <Badge>Plano</Badge>}
                </div>
              </div>
              {editable && (
                <div className="flex items-center justify-between gap-0.5 bg-surface px-1 py-1">
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Mover antes (foto ${String(index + 1)})`}
                    disabled={index === 0}
                    onClick={() => {
                      move(media.id, -1);
                    }}
                  >
                    <ArrowLeft />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Mover después (foto ${String(index + 1)})`}
                    disabled={index === items.length - 1}
                    onClick={() => {
                      move(media.id, 1);
                    }}
                  >
                    <ArrowRight />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Usar como portada (foto ${String(index + 1)})`}
                    disabled={media.id === coverId || media.isPrivate || media.kind === 'plan'}
                    onClick={() => {
                      makeCover(media.id);
                    }}
                  >
                    <Star />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-pressed={media.isPrivate}
                    aria-label={`${media.isPrivate ? 'Hacer pública' : 'Hacer privada'} (foto ${String(index + 1)})`}
                    onClick={() => void change(media, { isPrivate: !media.isPrivate })}
                  >
                    {media.isPrivate ? <LockOpen /> : <Lock />}
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-pressed={media.kind === 'plan'}
                    aria-label={`${media.kind === 'plan' ? 'Marcar como foto' : 'Marcar como plano'} (foto ${String(index + 1)})`}
                    onClick={() =>
                      void change(media, { kind: media.kind === 'plan' ? 'photo' : 'plan' })
                    }
                  >
                    <Ruler />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={`Eliminar foto ${String(index + 1)}`}
                    onClick={() => void remove(media)}
                  >
                    <Trash2 />
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {viewing?.largeUrl && (
        <Dialog
          open
          title={viewing.kind === 'plan' ? 'Plano' : 'Foto'}
          onClose={() => {
            setViewing(null);
          }}
          className="sm:w-[min(64rem,calc(100vw-2rem))]"
        >
          <img
            src={viewing.largeUrl}
            alt={viewing.caption ?? (viewing.kind === 'plan' ? 'Plano ampliado' : 'Foto ampliada')}
            className="max-h-[75vh] w-full rounded-lg object-contain"
          />
        </Dialog>
      )}
    </Card>
  );
}
